<mat-toolbar mat-dialog-title>{{ 'dds_preview_tr' | translate }}</mat-toolbar>

<mat-dialog-content style="padding: 1em 2em">
  <h3>{{ 'operator_name_tr' | translate }}</h3>
  <div style="margin-bottom: 1.5em">
    <div>
      <strong>{{ 'company_name_tr' | translate }}:</strong>
      {{ data.previewData.operatorName }}
    </div>
    <div>
      <strong>{{ 'eori_tr' | translate }}:</strong>
      {{ data.previewData.operatorEORI }}
    </div>
    <div>
      <strong>{{ 'address_tr' | translate }}:</strong>
      {{ data.previewData.operatorAddress.street || '-' }}, {{ data.previewData.operatorAddress.zip || '-' }}
      {{ data.previewData.operatorAddress.city || '-' }},
      {{ data.previewData.operatorAddress.country }}
    </div>
  </div>

  <h3 style="margin-top: 1.5em">{{ 'product_specification_dds_tr' | translate }}</h3>
  <dl style="margin-bottom: 1.5em">
    <dt style="font-weight: bold">{{ 'commodity_description_tr' | translate }}:</dt>
    <dd>{{ data.previewData.commodity.description }}</dd>
    <dt style="font-weight: bold">{{ 'hs_code_tr' | translate }}:</dt>
    @if (isHsCodeValid) {
      <dd>{{ hsCode }}</dd>
    } @else {
      <dd class="hs-code-invalid" style="color: #c62828; font-weight: bold">
        {{ (hsCode ? 'hs_code_invalid_tr' : 'hs_code_missing_tr') | translate: { hsCode } }}
      </dd>
    }
    <dt style="font-weight: bold">{{ 'weight_tr' | translate }}:</dt>
    <dd>{{ data.previewData.commodity.weight }} kg</dd>
    <dt style="font-weight: bold">{{ 'activity_type_tr' | translate }}:</dt>
    <dd>{{ 'activity_type_' + data.previewData.activityType.toLowerCase() + '_tr' | translate }}</dd>
  </dl>

  @if (supplementaryUnit?.required) {
    <mat-form-field appearance="outline" style="width: 100%; margin-bottom: 1em">
      <mat-label>{{ 'supplementary_unit_volume_tr' | translate }} ({{ unitLabel }})</mat-label>
      <input matInput type="number" min="0" step="any" [(ngModel)]="volume" name="supplementaryUnit" required />
      <span matTextSuffix>{{ unitLabel }}</span>
      @if (!isVolumeValid) {
        <mat-error>{{ 'supplementary_unit_required_tr' | translate }}</mat-error>
      }
    </mat-form-field>
  }

  <h3 style="margin-top: 1.5em">{{ 'raw_material_origins_geodata_tr' | translate }}</h3>
  @if (data.previewData.commodity.geodata && data.previewData.commodity.geodata.length > 0) {
    <table style="width: 100%; border-collapse: collapse; margin-bottom: 1.5em; font-size: 0.9em">
      <thead>
        <tr style="background: #f5f5f5; border-bottom: 2px solid #ddd">
          <th style="padding: 0.5em; text-align: left">{{ 'name_tr' | translate }}</th>
          <th style="padding: 0.5em; text-align: left">{{ 'country_tr' | translate }}</th>
          <th style="padding: 0.5em; text-align: left">{{ 'species_tr' | translate }}</th>
          <th style="padding: 0.5em; text-align: center; width: 60px">{{ 'geodata_tr' | translate }}</th>
          <th style="padding: 0.5em; text-align: center"></th>
        </tr>
      </thead>
      <tbody>
        @for (producer of data.previewData.commodity.geodata; track $index) {
          <tr style="border-bottom: 1px solid #eee">
            <td style="padding: 0.5em">{{ producer.name }}</td>
            <td style="padding: 0.5em">
              @if (producer.country) {
                {{ producer.country }}
              } @else {
                <span data-test="producer-country-missing" style="color: #c62828">✗</span>
              }
            </td>
            <td style="padding: 0.5em">
              @if (producer.species?.length) {
                <app-species-names [codes]="producer.species" />
              } @else {
                <span data-test="producer-species-missing" style="color: #c62828">✗</span>
              }
            </td>
            <td style="padding: 0.5em; text-align: center">
              <!-- Flex + gap + a fixed-width mark + a reserved icon slot: ✓ and ✗ differ in width and
                   a row without ⚠ is narrower, so centring alone let the column drift by up to 10px. -->
              <span class="geo-cell" style="display: inline-flex; align-items: center; gap: 4px">
                @if (producer.geoJson) {
                  <span class="geo-mark" style="width: 12px; text-align: center">✓</span>
                } @else {
                  <span class="geo-mark" data-test="producer-geo-missing" style="width: 12px; text-align: center; color: #c62828">✗</span>
                }
                @if (isIncomplete(producer)) {
                  <mat-icon
                    color="warn"
                    data-test="producer-gap-warning"
                    [matTooltip]="gapTooltip(producer)"
                    style="font-size: 16px; width: 16px; height: 16px; vertical-align: text-bottom"
                  >
                    report_problem
                  </mat-icon>
                }
                @if (producer.geoJson && producer.geoCheckSevere) {
                  <!-- Informed, never a gate (D9/app#233) — canSubmit stays untouched. -->
                  <mat-icon
                    color="warn"
                    data-test="producer-geo-severe-warning"
                    [matTooltip]="'producer_geodata_invalid_tr' | translate"
                    style="font-size: 16px; width: 16px; height: 16px; vertical-align: text-bottom"
                  >
                    report_problem
                  </mat-icon>
                }
                <app-site-data-badge [producer]="true" [whisp]="whispGapState(producer)" />
                @if (!isIncomplete(producer) && !(producer.geoJson && producer.geoCheckSevere)) {
                  <span class="geo-icon-slot" style="width: 16px"></span>
                }
              </span>
            </td>
            <td style="padding: 0.5em; text-align: center">
              @if (producer.geoJson) {
                <button
                  type="button"
                  mat-icon-button
                  class="row-icon-button"
                  (click)="dialog.viewGeoJsonMap(producer.geoJson)"
                  [title]="'view_location_on_map_tr' | translate"
                >
                  <mat-icon>map</mat-icon>
                </button>
              }
              @if (isIncomplete(producer)) {
                @if (gapLink(producer); as link) {
                  <a
                    mat-icon-button
                    class="row-icon-button"
                    [routerLink]="link"
                    [queryParams]="{ focus: focusGap(producer) }"
                    [title]="'maintain_producer_data_tr' | translate"
                    (click)="cancel()"
                  >
                    <mat-icon>edit_location_alt</mat-icon>
                  </a>
                } @else {
                  <!-- Visible text, not a tooltip: a hover title is unreachable on touch and the
                       row must say where the gap lives. -->
                  <span
                    class="producer-gap-no-link"
                    style="display: inline-flex; align-items: center; gap: 0.25em; color: #777; text-align: left"
                  >
                    <mat-icon style="font-size: 18px; width: 18px; height: 18px; color: #bbb">block</mat-icon>
                    <span>
                      {{
                        (supplierName(producer) ? 'producer_gap_supplier_tr' : 'producer_gap_supplier_unknown_tr')
                          | translate: { supplier: supplierName(producer) }
                      }}
                    </span>
                  </span>
                }
              }
            </td>
          </tr>
        }
      </tbody>
    </table>
  } @else {
    <p style="color: #999; font-style: italic">{{ 'no_producers_with_geodata_tr' | translate }}</p>
  }

  <h3 style="margin-top: 1.5em">{{ 'linked_dds_tr' | translate }}</h3>
  <div style="margin-bottom: 1.5em">
    @for (statement of data.previewData.associatedStatements; track statement.ddr) {
      <dl style="margin: 0.5em 0; padding: 0.5em; background: #f5f5f5; border-radius: 4px">
        <dt style="display: inline; font-weight: bold">DDR:</dt>
        <dd style="display: inline; margin: 0">{{ statement.ddr }}</dd>
        |
        <dt style="display: inline; font-weight: bold">DDV:</dt>
        <dd style="display: inline; margin: 0">{{ statement.ddv }}</dd>
      </dl>
    } @empty {
      <p style="color: #999; font-style: italic">{{ 'no_linked_dds_tr' | translate }}</p>
    }
  </div>
  <mat-divider style="margin: 1.5em 0"></mat-divider>

  @if (hasIncompleteProducer) {
    <p class="producer-data-incomplete-warning" style="color: #c62828; font-weight: bold">
      {{ 'producer_data_incomplete_tr' | translate }}
    </p>
  }

  @if (hasWhispGap) {
    <p class="whisp-gap-warning" style="color: #c62828; font-weight: bold">
      {{ 'whisp_dds_blocked_tr' | translate: { sites: whispGapSiteNames } }}
    </p>
  }

  <p class="dds-declaration">{{ 'dds_submit_declaration_tr' | translate }}</p>

  <mat-checkbox [(ngModel)]="confirmed" color="primary" style="margin: 1em 0">
    {{ 'i_confirm_the_information_is_correct_tr' | translate }}
  </mat-checkbox>
</mat-dialog-content>

<dialog-form-actions>
  @if (data.previewData.eudrCredentialName) {
    <!-- The action bar centres its items; a margin-top here pushed label + chip ~4px below the
         buttons. inline-flex drops the text space after the label, which the gap restores. -->
    <div class="eudr-credential" style="display: inline-flex; align-items: center; gap: 0.25em">
      <strong>{{ 'eudr_credential_tr' | translate }}:</strong>
      {{ data.previewData.eudrCredentialName }}
      <!-- Material colours the label itself: a plain `color` never reaches it, and its token is
           only beaten inline. The darker shades keep white text at ≥ 4.5:1 (#4caf50 was 2.8:1). -->
      <mat-chip
        [style.background-color]="data.previewData.eudrCredentialTarget === 'ACCEPTANCE' ? '#2e7d32' : '#1565c0'"
        style="margin-left: 0.5em; --mat-chip-label-text-color: white; font-size: 0.75em; height: 24px; padding: 0 8px"
      >
        <mat-icon style="font-size: 16px; height: 16px; width: 16px; margin-right: 4px; vertical-align: middle">
          {{ data.previewData.eudrCredentialTarget === 'ACCEPTANCE' ? 'science' : 'verified' }}
        </mat-icon>
        {{ data.previewData.eudrCredentialTarget }}
      </mat-chip>
    </div>
  }

  <button type="button" (click)="submit()" color="primary" mat-raised-button [disabled]="!canSubmit">
    {{ 'submit_dds_tr' | translate }}
  </button>
  <button type="button" (click)="cancel()" mat-raised-button>{{ 'cancel_tr' | translate }}</button>
</dialog-form-actions>
